/**
 * Custom icon components for icons not available in @untitledui/icons.
 *
 * These components match the UntitledUI icon interface (accepting className)
 * so they can be used interchangeably.
 */

import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

/**
 * LinkedIn brand icon.
 * Used for LinkedIn URL properties and profile links.
 */
/**
 * Gmail brand icon with multi-color paths.
 * Used for Gmail/email action buttons.
 */
export function GmailIcon({ className, ...props }: IconProps) {
  return (
    <svg
      className={className}
      viewBox='52 42 88 66'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden='true'
      {...props}
    >
      <path fill='#4285f4' d='M58 108h14V74L52 59v43c0 3.32 2.69 6 6 6' />
      <path fill='#34a853' d='M120 108h14c3.32 0 6-2.69 6-6V59l-20 15' />
      <path fill='#fbbc04' d='M120 48v26l20-15v-8c0-7.42-8.47-11.65-14.4-7.2' />
      <path fill='#ea4335' d='M72 74V48l24 18 24-18v26L96 92' />
      <path
        fill='#c5221f'
        d='M52 51v8l20 15V48l-5.6-4.2c-5.94-4.45-14.4-.22-14.4 7.2'
      />
    </svg>
  );
}

/**
 * Webhook icon (three connected nodes with curved arms).
 * Used for automation webhook entity types.
 * Scaled from SVG Repo source to 24×24 using a viewBox that
 * tightly crops the original 1014×1024 artwork, so the browser
 * rasterises the filled path at native resolution without aliasing.
 */
export function WebhookIcon({ className, ...props }: IconProps) {
  return (
    <svg
      width='24'
      height='24'
      viewBox='40 200 940 830'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
      aria-hidden='true'
      {...props}
    >
      <path
        fill='currentColor'
        d='M482 226h-1l-10 2q-33 4-64.5 18.5t-55.5 38.5q-41 37-57 91q-9 30-8 63t12 63q17 45 52 78l13 12-83 135q-26-1-45 7-30 13-45 40-7 15-9 31t2 32q8 30 33 48 15 10 33 14.5t36 2 34.5-12.5 27.5-25q12-17 14.5-39t-5.5-41q-1-5-7-14l-3-6 118-192q6-9 8-14l-10-3q-9-2-13-4-23-10-41.5-27.5t-28.5-39.5q-17-36-9-75 4-23 17-43t31-34q37-27 82-27 27-1 52.5 9.5t44.5 30.5q17 16 26.5 38.5t10.5 45.5q0 17-6 42l70 19 8 1q14-43 7-86-4-33-19.5-63.5t-39.5-53.5q-42-42-103-56-6-2-18-4l-14-2h-37zm18 124q-17 0-34 7t-30.5 20.5-19.5 31.5q-8 20-4 44 3 18 14 34t28 25q24 15 56 13 3 4 5 8l112 191q3 6 6 9 27-26 58.5-35.5t65-3.5 58.5 26q32 25 43.5 61.5t.5 73.5q-8 28-28.5 50t-48.5 33q-31 13-66.5 8.5t-63.5-24.5q-4-3-13-10l-5-6q-4 3-11 10l-47 46q23 23 52 38.5t61 21.5l22 4h39l28-5q64-13 110-60 22-22 36.5-50.5t19.5-59.5q5-36-2-71.5t-25-64.5-44-51-57-35q-34-14-70.5-16t-71.5 7l-17 5-81-137q13-19 16-37 5-32-13-60-16-25-44-35-17-6-35-6zM218 614q-58 13-100 53-47 44-61 105l-4 24v37l2 11q2 13 4 20 7 31 24.5 59t42.5 49q50 41 115 49 38 4 76-4.5t70-28.5q53-34 78-91 7-17 14-45 6-1 18 0l125 2q14 0 20 1 11 20 25 31t31.5 16 35.5 4q28-3 50-20 27-21 32-54 2-17-1.5-33t-13.5-30q-16-22-41-32-17-7-35.5-6.5t-35.5 7.5q-28 12-43 37l-3 6q-14 0-42-1l-113-1q-15-1-43-1l-50-1 3 17q8 43-13 81-14 27-40 45t-57 22q-35 6-70-7.5t-57-42.5q-28-35-27-79 1-37 23-69 13-19 32-32t41-19l9-3z'
      />
    </svg>
  );
}

/**
 * Pipeline icon (three stacked funnel tiers).
 * The canonical glyph for pipelines across the app — the create_pipeline
 * action/proposal, pipeline settings, the deals zero state, and the
 * ahoy_deal_pipeline entity. Kept here (not in the proposals registry) so every
 * layer can import it without crossing feature boundaries.
 */
export function PipelineIcon({ className, ...props }: IconProps) {
  return (
    <svg
      className={className}
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
      {...props}
    >
      <path d='M3 3h18v6H3z' />
      <path d='M5 9h14v6H5z' />
      <path d='M7 15h10v6H7z' />
    </svg>
  );
}

export function LinkedInIcon({ className, ...props }: IconProps) {
  return (
    <svg
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
      {...props}
    >
      <g clipPath='url(#linkedin-clip)'>
        <path
          d='M22.2234 0H1.77187C0.792187 0 0 0.773438 0 1.72969V22.2656C0 23.2219 0.792187 24 1.77187 24H22.2234C23.2031 24 24 23.2219 24 22.2703V1.72969C24 0.773438 23.2031 0 22.2234 0ZM7.12031 20.4516H3.55781V8.99531H7.12031V20.4516ZM5.33906 7.43438C4.19531 7.43438 3.27188 6.51094 3.27188 5.37187C3.27188 4.23281 4.19531 3.30937 5.33906 3.30937C6.47813 3.30937 7.40156 4.23281 7.40156 5.37187C7.40156 6.50625 6.47813 7.43438 5.33906 7.43438ZM20.4516 20.4516H16.8937V14.8828C16.8937 13.5562 16.8703 11.8453 15.0422 11.8453C13.1906 11.8453 12.9094 13.2937 12.9094 14.7891V20.4516H9.35625V8.99531H12.7687V10.5609H12.8156C13.2891 9.66094 14.4516 8.70938 16.1813 8.70938C19.7859 8.70938 20.4516 11.0813 20.4516 14.1656V20.4516Z'
          fill='currentColor'
        />
      </g>
      <defs>
        <clipPath id='linkedin-clip'>
          <rect width='24' height='24' fill='white' />
        </clipPath>
      </defs>
    </svg>
  );
}
